import React, { useState, useRef, useEffect } from 'react';
import MessageBubble from './MessageBubble';
import StreamingStatus, { getStatusMessage, getCompletionSummary } from './StreamingStatus';
import { sendMessageStream, appendMessagesAPI } from '../services/api';
import { Session, Location, Message, StreamEvent, StreamEventType, ToolData } from '../types';
import './ChatWindow.css';

interface ChatWindowProps {
  session: Session | null | undefined;
  location: Location | null;
  locationError: string | null;
  onAddMessage: (sessionId: string, message: Message) => void;
  email: string;
  sidebarCollapsed: boolean;
  onExpandSidebar: () => void;
}

export default function ChatWindow({ session, location, locationError, onAddMessage, email, sidebarCollapsed, onExpandSidebar }: ChatWindowProps) {
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState<string>('');
  const [completionSummary, setCompletionSummary] = useState<string>('');
  const [streamedContent, setStreamedContent] = useState<string>('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [session?.messages, currentStep, completionSummary, streamedContent]);

  const handleSend = async () => {
    const query = input.trim();
    if (!query || !session || isLoading) return;

    const userMessage: Message = { role: 'user', content: query, timestamp: new Date().toISOString() };
    onAddMessage(session.id, userMessage);
    setInput('');
    setIsLoading(true);
    setCurrentStep('');
    setCompletionSummary('');
    setStreamedContent('');

    try {
      const { response, toolData, extraction, encodedPolyline } = await sendMessageStream(
        { query, sessionId: session.id, email, location },
        (event: StreamEvent) => {
          handleStreamEvent(event);
        }
      );

      console.log('[ChatWindow] Stream complete. EncodedPolyline:', encodedPolyline ? 'Present' : 'Not present');

      const assistantMessage: Message = {
        role: 'assistant',
        content: response || 'No response from server.',
        tool_data: toolData as ToolData[],
        encodedPolyline: encodedPolyline,
        extraction: extraction,
        timestamp: new Date().toISOString(),
      };
      onAddMessage(session.id, assistantMessage);

      const isNewTitle = session.title === 'New Chat';
      const title = isNewTitle
        ? query.slice(0, 40) + (query.length > 40 ? '...' : '')
        : undefined;
      appendMessagesAPI(email, session.id, [userMessage, assistantMessage], title).catch((err) =>
        console.error('[ChatWindow] Failed to persist messages:', err.message)
      );
    } catch (err: any) {
      const errorMessage: Message = {
        role: 'assistant',
        content: `Error: ${err.message}`,
        timestamp: new Date().toISOString(),
        isError: true,
      };
      onAddMessage(session.id, errorMessage);

      appendMessagesAPI(email, session.id, [userMessage, errorMessage]).catch((e) =>
        console.error('[ChatWindow] Failed to persist error message:', e.message)
      );
    } finally {
      setIsLoading(false);
      setCurrentStep('');
      setStreamedContent('');
      setTimeout(() => setCompletionSummary(''), 500);
    }
  };

  const handleStreamEvent = (event: StreamEvent) => {
    // Log if polyline is in the event (for debugging)
    if (event.encodedPolyline) {
      console.log('[ChatWindow] EncodedPolyline detected in stream event');
    }

    switch (event.type) {
      case StreamEventType.NODE_START:
        const nodeMessage = getStatusMessage(event.type, event.toolName, event.nodeName);
        setCompletionSummary('');
        setCurrentStep(nodeMessage);
        break;

      case StreamEventType.TOOL_START:
        const toolMessage = getStatusMessage(event.type, event.toolName, event.nodeName);
        setCompletionSummary('');
        setCurrentStep(toolMessage);
        break;

      case StreamEventType.STREAM_CHUNK:
        if (event.content) {
          setCurrentStep('');
          setCompletionSummary('');
          setStreamedContent((prev) => prev + event.content);
        }
        break;

      case StreamEventType.TOOL_COMPLETE:
        const summary = getCompletionSummary(event.summary, event.success);
        if (summary) {
          setCurrentStep('');
          setCompletionSummary(summary);
          setTimeout(() => setCompletionSummary(''), 2500);
        }
        break;

      case StreamEventType.NODE_COMPLETE:
        break;

      case StreamEventType.WORKFLOW_COMPLETE:
        setCurrentStep('');
        setCompletionSummary('');
        break;

      case StreamEventType.ERROR:
        setCurrentStep('An error occurred');
        setCompletionSummary('');
        break;
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = Math.min(ta.scrollHeight, 160) + 'px';
  };

  const messages = session?.messages || [];
  const isEmpty = messages.length === 0;

  return (
    <div className="chat-window">
      <div className="chat-topbar">
        <div className="topbar-title">
          {sidebarCollapsed && (
            <button className="sidebar-expand-btn" onClick={onExpandSidebar} title="Show sidebar">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <line x1="9" y1="3" x2="9" y2="21" />
              </svg>
            </button>
          )}
          <span className="topbar-icon">⚡</span>
          EV Trip Planner
        </div>
        <div className="location-badge">
          {location ? (
            <span className="location-ok">
              📍 {location.name || `${location.latitude.toFixed(3)}, ${location.longitude.toFixed(3)}`}
            </span>
          ) : locationError ? (
            <span className="location-err">📍 Location unavailable</span>
          ) : (
            <span className="location-loading">📍 Getting location...</span>
          )}
        </div>
      </div>

      <div className="messages-area">
        {isEmpty ? (
          <div className="welcome">
            <div className="welcome-icon">⚡</div>
            <h2 className="welcome-title">Plan Your EV Trip</h2>
            <p className="welcome-subtitle">
              Ask me about charging stations, routes, range estimates, or anything EV-related.
            </p>
            <div className="suggestions">
              {[
                'Plan a trip from Montréal to New York with the fewest charging stops.',
                'Show me CCS chargers along the route from Ottawa to Kingston.',
                'Find available high-power chargers (150kW+) on the way from Montréal to Trois-Rivières.',
                'Plan a trip from Toronto to Niagara Falls with a charging stop that has amenities nearby.',
              ].map((s) => (
                <button key={s} className="suggestion-chip" onClick={() => setInput(s)}>
                  {s}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <>
            {messages.map((msg, i) => (
              <MessageBubble key={i} message={msg} />
            ))}
            {isLoading && (
              streamedContent ? (
                <MessageBubble
                  message={{ role: 'assistant', content: streamedContent, timestamp: new Date().toISOString() }}
                  isStreaming
                />
              ) : (
                <div className="assistant-typing-container">
                  <div className="avatar assistant-avatar">⚡</div>
                  <StreamingStatus
                    currentStep={currentStep}
                    isStreaming={isLoading}
                    streamedContent=""
                    completionSummary={completionSummary}
                  />
                </div>
              )
            )}
          </>
        )}
        <div ref={messagesEndRef} />
      </div>

      <div className="input-area">
        <div className="input-box">
          <textarea
            ref={textareaRef}
            className="input-textarea"
            placeholder="Ask about EV charging stations, trip planning..."
            value={input}
            onChange={handleInput}
            onKeyDown={handleKeyDown}
            rows={1}
          />
          <button
            className={`send-btn ${input.trim() && !isLoading ? 'active' : ''}`}
            onClick={handleSend}
            disabled={!input.trim() || isLoading}
            title="Send message"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="19" x2="12" y2="5" />
              <polyline points="5 12 12 5 19 12" />
            </svg>
          </button>
        </div>
        <p className="input-hint">Press Enter to send · Shift+Enter for new line</p>
      </div>
    </div>
  );
}
